import React from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import { Icon, RouteLink } from '~/components';
import FiltersDrawer from '~/components/filters/FiltersDrawer';
import { useWorkspace } from '~/contexts';
import { colors, weights } from '~/styles';

const Article = styled.article`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: fit-content;
  padding: 1rem;
`;

function Report(props) {
  return <Article {...props} />;
}

const Summary = styled.div`
  margin-top: 1rem;
  transition: opacity 250ms;
  opacity: ${({ fade }) => (fade ? 0.1 : 1)};
  position: sticky;
  left: 1rem;
  max-width: calc(100cqi - 2rem);
`;

const Status = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  position: sticky;
  left: 1rem;
  max-width: calc(100cqi - 2rem);

  .icon {
    font-size: 0.875rem;
  }

  > div {
    display: flex;
    align-items: center;

    &:only-child {
      margin-left: auto;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  position: sticky;
  left: 1rem;
  z-index: 6;
  max-width: calc(100cqi - 2rem);
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: ${weights.light};
  align-self: start;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Eyebrow = styled.h3`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.05rem;
  text-transform: uppercase;
  margin-top: 0.1rem;

  display: flex;
  gap: 0.375rem;
  align-items: center;

  .icon {
    font-size: 0.5rem;
  }
`;

const ReportDetails = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.grey55};
  font-size: 0.75rem;
  margin-top: 0.25rem;
  margin-right: 0.50rem;
`;

const ReportDetailItem = styled.span`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem 0.4rem;
  border-left: solid 1px ${colors.grey10};

  &:first-child {
    padding-left: 0;
    border-left: none;
  }

  &:last-child {
    padding-right: 0;
  }
`;

const BaseReport = styled(ReportDetailItem)`
  flex-shrink: 0;
  white-space: nowrap;
`;

const Description = styled(ReportDetailItem)`
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  margin-left: auto;
  gap: 1rem;
`;

const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
  grid-column-gap: 0.375rem;
  grid-row-gap: 0.75rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  position: sticky;
  left: 1rem;
  z-index: 5;
  max-width: calc(100cqi - 2rem);

  &:empty {
    display: none;
  }

  &,
  & span > strong {
    font-size: 0.875rem;
  }

  & input {
    height: 2rem;
    font-size: 0.75rem;
  }
`;

Report.Table = styled.div`
  margin-top: 1.5rem;
  margin-left: -1rem;
  margin-right: -1rem;
`;

function ReportInfo({ report }) {
  const { workspace } = useWorkspace();
  const { area } = useParams();

  let link;
  switch (area) {
    case 'my-reports':
      link = {
        label: 'My Reports',
        to: `/app/${workspace.key}/reports/my-reports`,
      };
      break;

    case 'library':
      link = {
        label: 'Library',
        to: `/app/${workspace.key}/reports/library`,
      };

      break;
  }

  const showBaseReportBadge = report.baseReport && report.scopeKey !== 'system';
  const baseReportUrl = showBaseReportBadge ? `/app/${workspace.key}/reports/${report.baseReport.slug}` : null;

  return (
    <Info>
      <Eyebrow>
        <RouteLink to={`/app/${workspace.key}/reports`}>Reports</RouteLink>
        <Icon icon="chevron-right" color={colors.grey40} />
        <RouteLink to={link.to}>{link.label}</RouteLink>
      </Eyebrow>
      <Title>{report.name}</Title>
      {(showBaseReportBadge || report.description) && (
        <ReportDetails>
          {showBaseReportBadge && (
            <BaseReport>
              <RouteLink to={baseReportUrl}>{report.baseReport.name}</RouteLink>
            </BaseReport>
          )}
          {report.description && <Description>{report.description}</Description>}
        </ReportDetails>
      )}
    </Info>
  );
}

Report.Header = Header;
Report.Info = ReportInfo;
Report.Actions = Actions;
Report.FiltersBar = FiltersBar;
Report.Summary = Summary;
Report.FiltersDrawer = FiltersDrawer;
Report.Status = Status;

export default Report;
